import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { MessageSquare, Send, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useWebSocket } from '@/hooks/useWebSocket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MessagingPage() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [newConversationDialog, setNewConversationDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchedUsers, setSearchedUsers] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const messagesEndRef = useRef(null);
  
  const token = localStorage.getItem('token');
  const { messages: wsMessages, isConnected, sendMessage } = useWebSocket(token);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  // Handle real-time messages
  useEffect(() => {
    const newMessages = wsMessages.filter(msg => msg.event === 'new_message');
    if (newMessages.length > 0) {
      const latest = newMessages[newMessages.length - 1];
      if (selectedConversation && latest.data.conversation_id === selectedConversation.id) {
        setMessages(prev => [...prev, latest.data.message]);
        scrollToBottom();
      }
      // Update conversation list
      fetchConversations();
    }
  }, [wsMessages, selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const fetchConversations = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/messaging/conversations`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setConversations(data.conversations || []);
    } catch (error) {
      toast.error('Failed to fetch conversations');
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/messaging/conversations/${conversationId}/messages`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setMessages(data.messages || []);
    } catch (error) {
      toast.error('Failed to fetch messages');
    }
  };

  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setSearchedUsers([]);
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/messaging/search-users?q=${encodeURIComponent(query)}&limit=10`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setSearchedUsers(data.users || []);
    } catch (error) {
      console.error('Failed to search users');
    }
  };

  const handleCreateConversation = async () => {
    if (selectedParticipants.length === 0) {
      toast.error('Please select at least one participant');
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/messaging/conversations`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ participant_ids: selectedParticipants })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Conversation created');
        setNewConversationDialog(false);
        setSelectedParticipants([]);
        setUserSearchQuery('');
        setSearchedUsers([]);
        fetchConversations();
        setSelectedConversation(data);
      } else {
        toast.error('Failed to create conversation');
      }
    } catch (error) {
      toast.error('Failed to create conversation');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/messaging/conversations/${selectedConversation.id}/messages`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ message: newMessage, message_type: 'text' })
      });

      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, data]);
        setNewMessage('');
        scrollToBottom();
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  const filteredConversations = conversations.filter(conv => 
    conv.participant_details?.some(p => 
      p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.email?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  return (
    <div className="p-8 h-[calc(100vh-80px)]" data-testid="messaging-page">
      <div className="flex h-full gap-4">
        {/* Conversations List */}
        <Card className="w-80 flex flex-col">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between mb-3">
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-[hsl(351,96%,31%)]" />
                Messages
              </CardTitle>
              <Dialog open={newConversationDialog} onOpenChange={setNewConversationDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" onClick={() => { setSelectedParticipants([]); setUserSearchQuery(''); setSearchedUsers([]); }}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>New Conversation</DialogTitle>
                    <DialogDescription>Search and select users to start a conversation</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Search users</Label>
                      <Input 
                        placeholder="Enter name or email" 
                        value={userSearchQuery}
                        onChange={(e) => {
                          setUserSearchQuery(e.target.value);
                          searchUsers(e.target.value);
                        }}
                      />
                    </div>
                    
                    {searchedUsers.length > 0 && (
                      <div className="border rounded-md p-2 max-h-48 overflow-y-auto">
                        <Label className="text-sm mb-2">Search Results</Label>
                        <div className="space-y-1 mt-2">
                          {searchedUsers.map((user) => (
                            <div
                              key={user.id}
                              onClick={() => {
                                if (!selectedParticipants.includes(user.id)) {
                                  setSelectedParticipants([...selectedParticipants, user.id]);
                                }
                              }}
                              className="p-2 hover:bg-muted cursor-pointer rounded flex items-center justify-between"
                            >
                              <div>
                                <div className="font-medium text-sm">{user.name}</div>
                                <div className="text-xs text-muted-foreground">{user.email} • {user.role}</div>
                              </div>
                              {selectedParticipants.includes(user.id) && (
                                <Badge variant="outline" className="text-xs">Selected</Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedParticipants.length > 0 && (
                      <div>
                        <Label className="text-sm">Selected Participants ({selectedParticipants.length})</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedParticipants.map((id) => {
                            const user = searchedUsers.find(u => u.id === id);
                            return user ? (
                              <Badge key={id} variant="outline" className="gap-1">
                                {user.name}
                                <button
                                  onClick={() => setSelectedParticipants(selectedParticipants.filter(p => p !== id))}
                                  className="ml-1 hover:text-destructive"
                                >
                                  ×
                                </button>
                              </Badge>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setNewConversationDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateConversation} disabled={selectedParticipants.length === 0}>
                      Start Chat
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search conversations..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </CardHeader>
          <ScrollArea className="flex-1">
            <div className="space-y-2 p-4 pt-0">
              {filteredConversations.map((conv) => {
                const otherParticipants = conv.participant_details?.filter(p => p.id !== currentUser.id) || [];
                const displayName = otherParticipants.map(p => p.name).join(', ') || 'Unknown';
                const isSelected = selectedConversation?.id === conv.id;

                return (
                  <div
                    key={conv.id}
                    onClick={() => setSelectedConversation(conv)}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      isSelected 
                        ? 'bg-[hsl(351,96%,31%,0.1)] border-2 border-[hsl(351,96%,31%)]' 
                        : 'hover:bg-muted border-2 border-transparent'
                    }`}
                    data-testid={`conversation-${conv.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarFallback className="bg-[hsl(351,96%,31%)] text-white">
                          {getInitials(displayName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium truncate">{displayName}</p>
                          {conv.unread_count > 0 && (
                            <Badge className="bg-[hsl(351,96%,31%)] text-white text-xs">
                              {conv.unread_count}
                            </Badge>
                          )}
                        </div>
                        {conv.last_message && (
                          <p className="text-sm text-muted-foreground truncate">
                            {conv.last_message.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {filteredConversations.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No conversations</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </Card>

        {/* Messages Area */}
        <Card className="flex-1 flex flex-col">
          {selectedConversation ? (
            <>
              <CardHeader className="pb-3 border-b">
                <CardTitle className="text-lg">
                  {selectedConversation.participant_details
                    ?.filter(p => p.id !== currentUser.id)
                    .map(p => p.name)
                    .join(', ') || 'Unknown'}
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  {isConnected ? '● Online' : '○ Offline'}
                </p>
              </CardHeader>
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.map((msg) => {
                    const isCurrentUser = msg.sender_id === currentUser.id;
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg px-4 py-2 ${
                            isCurrentUser
                              ? 'bg-[hsl(351,96%,31%)] text-white'
                              : 'bg-muted'
                          }`}
                        >
                          {!isCurrentUser && (
                            <p className="text-xs font-semibold mb-1">{msg.sender_name}</p>
                          )}
                          <p className="text-sm">{msg.message}</p>
                          <p className={`text-xs mt-1 ${
                            isCurrentUser ? 'text-white/70' : 'text-muted-foreground'
                          }`}>
                            {new Date(msg.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>
              <div className="p-4 border-t">
                <div className="flex gap-2">
                  <Input
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    data-testid="message-input"
                  />
                  <Button onClick={handleSendMessage} disabled={!newMessage.trim()} data-testid="send-message-btn">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">Select a conversation</p>
                <p className="text-sm">Choose a conversation from the left to start messaging</p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
